import React, { useCallback, useRef, useEffect } from "react";
import * as Scrivito from "scrivito";
import { Field, useFormikContext } from "formik";
import customProductImg from "assets/images/custom-item-picture.png";
import { Button } from "components";
import { useConfiguratorPopupContext } from "providers";
import { TextInput, CurrencyInput } from "components/Inputs";
import {
  MAX_CUSTOM_PRODUCT_TEXT_AREA_LENGTH,
  MAX_CUSTOM_PRODUCT_INPUT_LENGTH,
  MAX_CUSTOM_PRODUCT_LONG_INPUTP_LENGTH,
} from "variables";
import { useTranslate } from "hooks";
import type { FormikCustomProductValues } from "interfaces/product/product.types";

export const CustomProductBody = Scrivito.connect(
  ({
    isDisabled,
    customProductId,
  }: {
    isDisabled: boolean;
    customProductId: string;
  }) => {
    const translate = useTranslate();
    const { setIsUpdateButtonActive, handleUpdate, isUpdateButtonActive } =
      useConfiguratorPopupContext()!;

    const { values, setFieldValue, dirty } =
      useFormikContext<FormikCustomProductValues>();
    const lastValidTitleRef = useRef(values.title);

    useEffect(() => {
      if (customProductId) {
        setIsUpdateButtonActive(dirty);
      }
    }, [customProductId, dirty, setIsUpdateButtonActive]);

    const handleSaveButton = useCallback(() => {
      handleUpdate();
      setIsUpdateButtonActive(false);
    }, [handleUpdate, setIsUpdateButtonActive]);

    const handleBlur = () => {
      const currentTitle = values.title?.trim();

      if (currentTitle) {
        lastValidTitleRef.current = values.title;
      } else {
        setFieldValue("title", lastValidTitleRef.current);
      }
    };

    return (
      <div className="mb-2_5">
        <h5 className="mb-4 text-uppercase">{translate("DETAILS")}</h5>
        <div className="d-flex flex-column px-4 py-3 bg-white rounded-2">
          <div className="pt-2_5 d-flex row">
            <div className="col-12 col-lg-6">
              <Field
                component={TextInput}
                name="title"
                id="title"
                type="text"
                label={translate("CUSTOM_ITEM_NAME")}
                placeholder={translate.stringOnly("CUSTOM_ITEM_PLACEHOLDER")}
                disabled={isDisabled}
                onBlur={handleBlur}
                maxLength={MAX_CUSTOM_PRODUCT_INPUT_LENGTH}
              />
              <Field
                component={TextInput}
                name="ordercode"
                id="ordercode"
                type="text"
                placeholder={translate.stringOnly(
                  "WISHED_ORDERCODE_PLACEHOLDER",
                )}
                label={translate("ORDER_CODE")}
                disabled={isDisabled}
                maxLength={MAX_CUSTOM_PRODUCT_LONG_INPUTP_LENGTH}
              />
              <Field
                component={CurrencyInput}
                label={translate("PRICE_PER_PIECE")}
                id="price"
                name="price"
                disabled={isDisabled}
              />

              <Field
                component={TextInput}
                name="priceGroup"
                id="priceGroup"
                type="text"
                placeholder={translate.stringOnly(
                  "PRICE_GROUP_INPUT_PLACEHOLDER",
                )}
                label={translate("PRICE_GROUP_PRICE_LIST")}
                disabled={isDisabled}
                maxLength={MAX_CUSTOM_PRODUCT_LONG_INPUTP_LENGTH}
              />
              <div className="text-gray-700 fw-bolder mb-2">
                {translate("PRODUCT_PREVIEW")}
              </div>
              <div className="d-flex justify-content-center align-items-center flex-shrink-0 border border-gray-300 col-4">
                <img
                  src={customProductImg}
                  alt="Custom item"
                  data-testid="custom-item-picture"
                  className="my-3_5"
                />
              </div>
            </div>
            <div className="col-12 col-lg-6 flex-grow-1">
              <Field
                component={TextInput}
                type="textarea"
                name="description"
                id="description"
                placeholder={translate.stringOnly(
                  "DESCRIBE_PRODUCT_PLACEHOLDER",
                )}
                label={translate("DESCRIPTION")}
                wrapperClassName="h-100 mb-3 pb-2 d-flex flex-column"
                maxLength={MAX_CUSTOM_PRODUCT_TEXT_AREA_LENGTH}
                disabled={isDisabled}
                className="h-100"
              />
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end mt-3">
          {customProductId && (
            <Button
              color="primary"
              className="ms-2"
              onClick={handleSaveButton}
              disabled={!isUpdateButtonActive}
            >
              {translate("SAVE")}
            </Button>
          )}
        </div>
      </div>
    );
  },
);
